import React, { Component } from 'react'
import { connect } from 'react-redux'
import { NavigateFunction, useNavigate, useParams } from 'react-router-dom'

import { isEmpty } from 'lodash-es'
import { bindActionCreators } from 'redux'
import styled from 'styled-components/native'

import { getDisplayDate } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/dateUtils'
import DefaultButton from '@lyrahealth-inc/ui-core/lib/atoms/buttons/defaultButton/DefaultButton'
import LoadingIndicator from '@lyrahealth-inc/ui-core/lib/atoms/loadingIndicator/LoadingIndicator'
import ContentLayout from '@lyrahealth-inc/ui-core/lib/molecules/layouts/base/ContentLayout'
import BootstrapContainer from '@lyrahealth-inc/ui-core/lib/molecules/layouts/bootstrapContainer/BootstrapContainer'
import { Tooltip } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/tooltip/Tooltip'
import { BaseModal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/baseModal/BaseModal'

import * as paymentHistoryActions from './data/paymentHistoryActions'
import {
  getPaymentHistoryHistoryData,
  getPaymentHistoryModalData,
  getPaymentHistoryShowModal,
} from './data/paymentHistorySelectors'
import styles from './paymentHistory.module.scss'
import PaymentHistoryCard from './paymentHistoryCard/PaymentHistoryCard'
import DownloadCSVButton from '../common/components/downloadCSV/DownloadCSVButton'
import PaymentDetailsModal from '../common/components/paymentDetailsModal/PaymentDetailsModal'
import TabFilters from '../common/components/tabFilters/TabFilters'
import { ROLES } from '../common/constants/appConstants'
import { GET_PAYMENT_HISTORY_OF_TYPE } from '../common/constants/reduxConstants'
import {
  PAYMENT_HISTORY_ALL,
  PAYMENT_HISTORY_APPROVED,
  PAYMENT_HISTORY_CANCELLED,
  PAYMENT_HISTORY_REJECTED,
  PAYMENT_HISTORY_REVERSED,
  PAYMENT_HISTORY_SUBMITTED,
  REQUEST_PAYMENT,
} from '../common/constants/routingConstants'
import { axiosAuthInstance } from '../common/http/axiosInstance'
import { hasErrorForAction, hasRole } from '../common/utils/utils'
import { getAlertsState } from '../data/alertsSelectors'
import { getAuthPracticeId, getAuthRoles, getAuthUserAccessType, getAuthUserId } from '../data/auth/authSelectors'
import { RootState } from '../data/store'
import * as paymentsDataActions from '../payments/data/paymentsDataActions'

type PaymentHistoryProps = {
  showModal?: boolean
  actions?: any
  modalData?: any // TODO: PropTypes.instanceOf(Map)
  historyData?: any // TODO: PropTypes.instanceOf(Map)
  navigate: NavigateFunction
  params: { type?: string }
  roles?: any // TODO: PropTypes.instanceOf(List)
  providerID?: string
  practiceID?: string
  alerts?: any // TODO: PropTypes.instanceOf(List)
  providerAccessType: any
}

type PaymentHistoryState = any

type PaymentHistoryTooltipProps = {
  download: ($$values: any) => any
}

const CsvTooltipContainer = styled.View({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  flexDirection: 'row',
})

const PaymentHistoryTooltip: React.FC<PaymentHistoryTooltipProps> = ({ download }) => {
  return (
    <CsvTooltipContainer>
      <Tooltip
        content='You can download a csv file of all your invoices submitted by clicking on the CSV icon. Please note that you can only download a 1-month timeframe at a time.'
        hoverEnabled={true}
        wrapperStyle={{ marginRight: 4 }}
      />
      {/* @ts-expect-error TS(2322): Type '($$values: any) => any' is not assignable to... Remove this comment to see the full error message */}
      <DownloadCSVButton downloadClick={download} />
    </CsvTooltipContainer>
  )
}

class PaymentHistory extends Component<PaymentHistoryProps, PaymentHistoryState> {
  constructor(props: PaymentHistoryProps) {
    super(props)

    this.state = {
      loadingMore: false,
      noMoreForType: {
        all: false,
        submitted: false,
        approved: false,
        rejected: false,
        cancelled: false,
        reversed: false,
      },
    }
  }

  componentDidMount() {
    const type = this._getActivePaymentType()

    if (!this.props.historyData || !(type in this.props.historyData)) {
      this._getHistoryOfStatusForRole(type, '0')
    }
  }

  componentWillUnmount() {
    this.props.actions.clearPaymentHistoryStore()
    if (typeof (axiosAuthInstance as any).cancelLastRequest === 'function') {
      ;(axiosAuthInstance as any).cancelLastRequest()
    }
  }

  _onCopyClick = (chargeID: any) => {
    this.props.navigate(REQUEST_PAYMENT.route, { state: { copy_charge_id: chargeID } })
  }

  _onViewClick = (data: any) => {
    this.props.actions.showTransfersModal(data)
  }

  _closeModal = () => {
    this.props.actions.hideTransfersModal()
  }

  _loadMore = () => {
    const type = this._getActivePaymentType()
    const currentPage = this.props.historyData?.[type]?.page
    const nextPage = parseInt(currentPage, 10) + 1

    this.setState({
      loadingMore: true,
    })
    this._getHistoryOfStatusForRole(type, nextPage).then(
      (successObj: any) => {
        const noMores = this.state.noMoreForType
        noMores[type] = successObj.data.length === 0

        this.setState({
          loadingMore: false,
          noMoreForType: noMores,
        })
      },
      () => {
        this.setState({
          loadingMore: false,
        })
      },
    )
  }

  _getHistoryOfStatusForRole = (type: any, page: any) => {
    // status, page, limit, providerID, practiceID

    const status = type === 'all' ? undefined : type

    if (hasRole(this.props.roles, ROLES.PRACTICES_ADMIN)) {
      return this.props.actions.getPaymentsOfStatus({ status, page, practiceID: this.props.practiceID })
    } else if (hasRole(this.props.roles, ROLES.INDIVIDUAL_PROVIDER)) {
      return this.props.actions.getPaymentsOfStatus({ status, page, providerID: this.props.providerID })
    }
  }

  _getActivePaymentType = () => {
    return this.props.params.type ?? 'all'
  }

  _renderHistoryList = (type: any) => {
    const { historyData, roles, alerts } = this.props
    if (!historyData || !(type in historyData)) {
      if (hasErrorForAction(alerts, GET_PAYMENT_HISTORY_OF_TYPE)) {
        return null // custom workflow or display could render here
      } else {
        this._getHistoryOfStatusForRole(type, '0')
        return (
          <div className={styles['loading-container']}>
            <LoadingIndicator size={45} />
          </div>
        )
      }
    }

    const historyList = historyData[type].data
    if (historyList.length === 0) {
      return <div className={styles['loading-container']}>No payments found.</div>
    }

    return (
      <div>
        {historyList.map((value: any, i: any) => {
          const key = `$$historyData_${i}`
          return (
            <PaymentHistoryCard
              isPracticeAdmin={hasRole(roles, ROLES.PRACTICES_ADMIN)}
              data={value}
              key={key}
              copyClick={this._onCopyClick}
              viewClick={this._onViewClick}
            />
          )
        })}
      </div>
    )
  }

  _handleTabClick = (tab: any) => {
    switch (tab.toLowerCase()) {
      case 'all':
        this.props.navigate(PAYMENT_HISTORY_ALL.route, { replace: true })
        break
      case 'submitted':
        this.props.navigate(PAYMENT_HISTORY_SUBMITTED.route, { replace: true })
        break
      case 'approved':
        this.props.navigate(PAYMENT_HISTORY_APPROVED.route, { replace: true })
        break
      case 'rejected':
        this.props.navigate(PAYMENT_HISTORY_REJECTED.route, { replace: true })
        break
      case 'cancelled':
        this.props.navigate(PAYMENT_HISTORY_CANCELLED.route, { replace: true })
        break
      case 'reversed':
        this.props.navigate(PAYMENT_HISTORY_REVERSED.route, { replace: true })
        break
    }
  }

  _renderTabs = () => {
    let tab = this._getActivePaymentType()

    const tabWordArray: any = []
    tab.split('_').forEach((word: any) => {
      tabWordArray.push(word.charAt(0).toUpperCase() + word.slice(1))
    })
    tab = tabWordArray.join(' ')

    return (
      <TabFilters
        activeTab={tab}
        tabList={['ALL', 'SUBMITTED', 'APPROVED', 'REJECTED', 'CANCELLED', 'REVERSED']}
        className={styles.tabs}
        // @ts-expect-error TS(2322): Type '(tab: any) => void' is not assignable to typ... Remove this comment to see the full error message
        handleClick={this._handleTabClick}
      />
    )
  }

  _onCSVDownloadClick = ($$values: any) => {
    const startDate = getDisplayDate($$values.get('start_date'), 'YYYY-MM-DD')
    const endDate = getDisplayDate($$values.get('end_date'), 'YYYY-MM-DD')
    if (hasRole(this.props.roles, ROLES.PRACTICES_ADMIN)) {
      return this.props.actions.downloadPayments(
        $$values.get('payment_status'),
        startDate,
        endDate,
        undefined,
        this.props.practiceID,
      )
    } else if (hasRole(this.props.roles, ROLES.INDIVIDUAL_PROVIDER)) {
      return this.props.actions.downloadPayments(
        $$values.get('payment_status'),
        startDate,
        endDate,
        this.props.providerID,
      )
    }
  }

  render() {
    const { showModal, modalData, historyData, providerAccessType } = this.props
    if (!historyData) {
      return (
        <ContentLayout>
          <BootstrapContainer>
            <div className={styles['loading-container']}>
              <LoadingIndicator size={45} />
            </div>
          </BootstrapContainer>
        </ContentLayout>
      )
    }

    const type = this._getActivePaymentType()

    const ModalData = (
      <PaymentDetailsModal viewData={modalData} copyClick={this._onCopyClick} closeModal={this._closeModal} />
    )
    const hasPayments = !!historyData && !!historyData?.[type]?.data && !isEmpty(historyData[type].data)
    return (
      <div className={styles['page-container']}>
        <div className={styles['top-container']}>
          <h2>Payment History</h2>
          {hasPayments && <PaymentHistoryTooltip download={this._onCSVDownloadClick} />}
        </div>
        {providerAccessType !== 'T0' ? this._renderTabs() : []}
        {this._renderHistoryList(type)}
        {this.state.noMoreForType[type] ? (
          <div className={styles['end-of-list-spacer']} />
        ) : (
          <div className={styles['load-more']}>
            {this.state.loadingMore ? (
              <LoadingIndicator size={30} />
            ) : hasPayments ? (
              <DefaultButton onClick={this._loadMore}>Load More</DefaultButton>
            ) : (
              []
            )}
          </div>
        )}
        {/* @ts-expect-error TS(2322): Type 'boolean | undefined' is not assignable to ty... Remove this comment to see the full error message */}
        <BaseModal isOpen={showModal} body={ModalData} closeModal={this.props.actions.hideTransfersModal} />
      </div>
    )
  }
}

const mapStateToProps = (state: RootState) => {
  return {
    showModal: getPaymentHistoryShowModal(state),
    modalData: getPaymentHistoryModalData(state),
    historyData: getPaymentHistoryHistoryData(state),
    roles: getAuthRoles(state),
    providerID: getAuthUserId(state),
    practiceID: getAuthPracticeId(state),
    alerts: getAlertsState(state),
    providerAccessType: getAuthUserAccessType(state),
  }
}

const mapDispatchToProps = (dispatch: any) => {
  return {
    actions: bindActionCreators({ ...paymentHistoryActions, ...paymentsDataActions }, dispatch),
  }
}

const ConnectedPaymentHistory = connect(mapStateToProps, mapDispatchToProps)(PaymentHistory) as React.ComponentType<{
  navigate: NavigateFunction
  params: { type?: string }
}>

export default function PaymentHistoryWithRouter() {
  const navigate = useNavigate()
  const params = useParams<{ type?: string }>()
  return <ConnectedPaymentHistory navigate={navigate} params={params} />
}
